import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import "../../assets/matka/css/matka.css";
import socket from "../../assets/socket";
const base_url = process.env.REACT_APP_API_URL;

const amounts = [10, 50, 100, 500, 1000];

const numbers = [
  ...Array.from({ length: 99 }, (_, i) => String(i + 1).padStart(2, "0")),
  "00",
];

const getTodayDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const istDate = new Date(now.getTime() - offset * 60 * 1000);
  return istDate.toISOString().split("T")[0]; // YYYY-MM-DD
};

const getISTTimeNow = () => {
  const now = new Date();

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);
};

const andarMap = ["A0", "A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9"];
const baharMap = ["B0", "B1", "B2", "B3", "B4", "B5", "B6", "B7", "B8", "B9"];

const MatkaGame = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();

  const marketId = location.state?.marketId;
  const marketName = location.state?.marketName;
  const marketOpen = location.state?.marketOpen;
  const marketClose = location.state?.marketClose;
  const gameIdByState = location.state?.gameId;
  const dateByState = location.state?.date;

  const [amount, setAmount] = useState(10);
  const [isPlacing, setIsPlacing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [gameStatus, setGameStatus] = useState(0);
  const [betHistory, setBetHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clickToDeleteBet, setClickToDeleteBet] = useState(false);
  const [betPopupActive, setBetPopupActive] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [isLogged, setIsLogged] = useState(false);

  const [date, setDate] = useState(dateByState || "");
  const [gameId, setGameId] = useState(gameIdByState || "");

  /* 🔊 AUDIO */
  const audioFiles = {
    click: new Audio("/assets/titli/audio/click.mp3"),
    bet: new Audio("/assets/titli/audio/bet.mp3"),
  };

  const playSound = (audio) => {
    try {
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch {}
  };

  /* FETCH USER BALANCE */
  const fetchUserBalance = async () => {
    try {
      const res = await axios.get(`${base_url}/api/matka/user/balance`, {
        headers: {
          Authorization: token,
        },
      });

      if (res.data.message === "Auth is reuired..") {
        setIsLogged(false);
        return;
      }
      if (res.data.success) {
        setUserBalance(res.data.balance);
        setIsLogged(true);
      }
    } catch {
      console.log("Failed to load user balance");
    } finally {
      setLoading(false);
    }
  };

  /* 🔐 SAFETY */
  useEffect(() => {
    fetchUserBalance();
    if (!marketId) {
      toast.error("Invalid market");
      navigate("/matka-list");
    }
  }, []);

  const finalBetMap = useMemo(() => {
    const map = {};

    betHistory.forEach((b) => {
      if (b.status === 0) {
        const key = String(b.bet_value);
        map[key] =
          (map[key] || 0) +
          Number(b.bet_amount || 0) +
          Number(b.bet_commission || 0);
      }
    });

    return map;
  }, [betHistory]);

  /* ADD BET */
  const addBet = (num) => {
    isLogged === false && toast.error("Please login to place bet!");

    if (gameStatus === 0) return;

    setSelectedNumber(num);
    setBetPopupActive(true);
  };

  const sumKeys = (keys) =>
    keys.reduce((sum, k) => sum + (finalBetMap[k] || 0), 0);

  const getColumnTotal = (colIndex) => {
    let total = 0;

    for (let row = 0; row < 10; row++) {
      const numKey = String(row * 10 + (colIndex + 1)).padStart(2, "0");
      total += finalBetMap[numKey] || 0;
    }

    total += finalBetMap[andarMap[colIndex]] || 0;
    total += finalBetMap[baharMap[colIndex]] || 0;

    return total;
  };

  const grandTotal = useMemo(
    () => Object.values(finalBetMap).reduce((a, b) => a + b, 0),
    [finalBetMap],
  );

  /* 🎯 PLACE BET */
  const placeBet = async () => {
    await fetchGameStatus();

    if (gameStatus !== 1) {
      toast.error("Market is Closed");
      return;
    }

    if (!selectedNumber || amount <= 0) {
      toast.error("Select number & amount");
      return;
    }

    setIsPlacing(true);

    try {
      const res = await axios.post(
        `${base_url}/api/matka/place-bet`,
        {
          market_id: marketId,
          game_id: gameId,
          bets: [
            {
              bet_value: selectedNumber,
              amount: amount,
            },
          ],
          total_amount: amount,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (res.data.success) {
        toast.success("Bet placed");

        setSelectedNumber(null);
        setBetPopupActive(false);

        // 🔥 ONLY THESE
        fetchBetHistory(1);
        fetchUserBalance();
      }
    } catch {
      toast.error("Bet failed");
    } finally {
      setIsPlacing(false);
    }
  };

  const fetchBetHistory = async (p = 1) => {
    try {
      const res = await axios.get(
        `${base_url}/api/matka/user-side/bet-history?market_id=${marketId}&game_id=${gameId}&page=${p}&limit=1000`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (res.data.success) {
        setBetHistory(res.data.bets || []);
        setTotalPages(res.data.pagination.totalPages || 1);
        setPage(p);
      }
    } catch {
      console.log("Failed to load bet history");
    }
  };

  const fetchGameStatus = async () => {
    try {
      const res = await axios.get(
        `${base_url}/api/admin/matka/current-game?market_id=${marketId}&date=${date}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (res.data.success) {
        setGameStatus(Number(res.data.game.status));

        if (!gameIdByState) {
          setGameId(res.data.game.game_id);
        }
      }
    } catch {
      toast.error("Failed to load game status");
    }
  };

  useEffect(() => {
    if (!marketId) {
      toast.error("Invalid market");
      navigate("/matka-list");
      return;
    }

    fetchUserBalance();

    // ✅ if gameId came from calendar, do NOT refetch game
    if (dateByState === getTodayDate() || !gameIdByState) {
      fetchGameStatus();
    }
  }, []);

  useEffect(() => {
    if (gameId) {
      fetchBetHistory(1);
    }
  }, [gameId]);

  useEffect(() => {
    socket.on("matka_result_update", () => {
      fetchGameStatus();
      fetchUserBalance();
      fetchBetHistory(1);
    });

    socket.on("matka_status_update", () => {
      fetchGameStatus();
    });

    return () => {
      socket.off("matka_result_update");
    };
  }, []);

  const currentClickTotal = amount;

  const deleteBetBackend = async (type, betValue = null) => {
    if (gameStatus !== 1) {
      toast.error("Market is Closed");
      return;
    }

    try {
      const res = await axios.post(
        `${base_url}/api/matka/delete-bet`,
        {
          game_id: gameId,
          type,
          bet_value: betValue,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (res.data.success) {
        toast.success(res.data.message || "Bet deleted");
        fetchUserBalance();
        fetchBetHistory(1);
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const currentIST = getISTTimeNow();

      if (currentIST === marketClose) {
        // Run these ONLY ONCE at market close time
        fetchGameStatus();
        fetchBetHistory(1);
        fetchUserBalance();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [marketClose]);


  return (
    <div className="matka-screen">
      {/* HEADER */}
      <div className="matka-header">
        <div className="matka-header-left">
          <ArrowLeft
            onClick={() => {
              playSound(audioFiles.click);
              navigate(-1);
            }}
          />
          <div>
            <h3>{marketName || "Matka Game"}</h3>
            <p style={{ margin: 0 }}>{date}</p>
          </div>
        </div>
        <div className="matka-header-right">
          <h3>POINTS: {parseFloat(userBalance).toFixed(2)}</h3>
        </div>
      </div>

      <div className="matka-content">
        {loading && <p className="text-center">Loading...</p>}

        <div className="commission-note">
          🔥 ₹10 पर ₹900 जीतें | हर बेट पर 7% कमीशन
        </div>

        {/* DELETE BAR */}
        {gameStatus === 1 && (
          <div className="bet-delete-bar">
            <button
              className="delete-btn"
              onClick={() => {
                deleteBetBackend("last");
              }}
            >
              Delete Last Bet
            </button>

            <button
              className={`delete-btn ${clickToDeleteBet ? "active" : ""}`}
              onClick={() => setClickToDeleteBet((prev) => !prev)}
            >
              {clickToDeleteBet ? "Cancel Delete Mode" : "Click To Delete"}
            </button>

            <button
              className="delete-btn danger"
              onClick={() => {
                deleteBetBackend("all");
              }}
            >
              Delete All Bets
            </button>
          </div>
        )}

        {/* NUMBER GRID */}
        <div className="number-grid jantri-grid">
          {/* 1–100 */}
          {Array.from({ length: 10 }).map((_, rowIndex) => {
            const rowNums = numbers.slice(rowIndex * 10, rowIndex * 10 + 10);
            const rowTotal = sumKeys(rowNums);

            return (
              <React.Fragment key={rowIndex}>
                {rowNums.map((num) => (
                  <button
                    key={num}
                    className={`number-btn ${finalBetMap[num] ? "active" : ""}`}
                    onClick={() => {
                      playSound(audioFiles.click);

                      if (clickToDeleteBet) {
                        deleteBetBackend("single", num);
                        setClickToDeleteBet(false);
                      } else {
                        addBet(num);
                      }
                    }}
                  >
                    {num}
                    {finalBetMap[num] && (
                      <span className="bet-amount">{finalBetMap[num]}</span>
                    )}
                  </button>
                ))}
                <div className="row-total-box">{rowTotal || ""}</div>
              </React.Fragment>
            );
          })}

          {/* A0–A9 */}
          {andarMap.map((num) => (
            <button
              key={num}
              className="number-btn"
              onClick={() => {
                playSound(audioFiles.click);

                if (clickToDeleteBet) {
                  deleteBetBackend("single", num);
                  setClickToDeleteBet(false);
                } else {
                  addBet(num);
                }
              }}
            >
              {num}
              {finalBetMap[num] && (
                <span className="bet-amount">{finalBetMap[num]}</span>
              )}
            </button>
          ))}
          <div className="row-total-box">{sumKeys(andarMap) || ""}</div>

          {/* B0–B9 */}
          {baharMap.map((num) => (
            <button
              key={num}
              className="number-btn"
              onClick={() => {
                playSound(audioFiles.click);

                if (clickToDeleteBet) {
                  deleteBetBackend("single", num);
                  setClickToDeleteBet(false);
                } else {
                  addBet(num);
                }
              }}
            >
              {num}
              {finalBetMap[num] && (
                <span className="bet-amount">{finalBetMap[num]}</span>
              )}
            </button>
          ))}
          <div className="row-total-box">{sumKeys(baharMap) || ""}</div>

          {/* COLUMN TOTALS */}
          {Array.from({ length: 10 }).map((_, col) => (
            <div key={col} className="row-total-box">
              {getColumnTotal(col) || ""}
            </div>
          ))}
          <div className="row-total-box">{grandTotal || ""}</div>
        </div>

        {/* AMOUNT */}
        {gameStatus === 1 && betPopupActive === true ? (
          <>
            {/* SELECT AMOUNT */}
            <div className="game-card-main">
              <div className="game-card amount-card">
                <h4 className="amount-title">Selected Bet: {selectedNumber}</h4>

                {/* Preset buttons */}
                <div className="amount-row">
                  {amounts.map((amt) => (
                    <button
                      key={amt}
                      className={`amount-btn ${amount === amt ? "active" : ""}`}
                      onClick={() => {
                        playSound(audioFiles.click);
                        setAmount(amt);
                      }}
                    >
                      ₹{amt}
                    </button>
                  ))}
                </div>

                {/* Manual input */}
                <div className="custom-amount-row">
                  <span className="rupee-prefix">₹</span>
                  <input
                    type="number"
                    min="1"
                    value={amount}
                    onChange={(e) => {
                      setAmount(Number(e.target.value) || 0);
                    }}
                    placeholder="Enter amount"
                    className="amount-input"
                  />
                </div>

                {/* Divider */}
                <div className="amount-divider" />

                {/* Place Bet */}
                <div className="place-bet-row">
                  <div
                    className="cancelBtn"
                    onClick={() => {
                      setSelectedNumber(null);
                      setBetPopupActive(false);
                    }}
                  >
                    ❎ Cancel
                  </div>

                  <button
                    className="place-bet-btn"
                    onClick={placeBet}
                    disabled={isPlacing || amount === 0}
                  >
                    {isPlacing ? (
                      <span className="loading-text">Placing Bet…</span>
                    ) : (
                      <>
                        <span>Place Bets</span>
                        <strong>₹{currentClickTotal}</strong>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          ""
        )}

        <div className="game-card mt-4">
          <h4>My Bet History</h4>

          {betHistory.length === 0 ? (
            <p className="text-center text-dark">No bets available</p>
          ) : (
            <div className="table-responsive">
              <table className="bet-history-table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Bet</th>
                    <th>Commission</th>
                    <th>Deduct</th>
                    <th>Win</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {betHistory.map((b) => (
                    <tr
                      key={b.id}
                      className={
                        b.status === 1
                          ? "row-win"
                          : b.status === 2
                            ? "row-lose"
                            : "row-pending"
                      }
                      style={
                        /^[AB][0-9]$/.test(b.bet_value)
                          ? { backgroundColor: "rgb(94 26 205)" }
                          : {}
                      }
                    >
                      <td className="bet-number">{b.bet_value}</td>

                      <td className="bet-amount">
                        ₹{Number(b.bet_amount) + Number(b.bet_commission)}
                      </td>

                      <td className="bet-amount">
                        ₹{Number(b.bet_commission).toFixed(2)}
                      </td>

                      <td className="bet-amount">
                        ₹{Number(b.bet_amount).toFixed(2)}
                      </td>

                      <td
                        className={
                          b.status === 1 ? "win-amount" : "lose-amount"
                        }
                      >
                        ₹{Number(b.win_amount || 0).toFixed(2)}
                      </td>

                      <td>
                        <span
                          className={`status-badge ${
                            b.status === 1
                              ? "status-win"
                              : b.status === 2
                                ? "status-lose"
                                : "status-pending"
                          }`}
                        >
                          {b.status === 1
                            ? "WIN"
                            : b.status === 2
                              ? "LOSE"
                              : "PENDING"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={page <= 1}
                onClick={() => {
                  playSound(audioFiles.click);
                  fetchBetHistory(page - 1);
                }}
              >
                Prev
              </button>
              <span>
                Page {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => {
                  playSound(audioFiles.click);
                  fetchBetHistory(page + 1);
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatkaGame;
